import { Link } from 'wouter';
import { useTranslation } from 'react-i18next';
import { ArrowRight } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { useDetectedCurrency } from '@/hooks/useCurrency';
import { Eyebrow, Section, SectionTitle, type FaqItem } from './layout';

/** An answer that carries {{startingPrice}} also links to the full pricing page. */
const PRICE_PLACEHOLDER = '{{startingPrice}}';

interface LandingFaqProps {
  /** i18n namespace of the page. */
  ns: string;
  /** Key prefix holding eyebrow, title, items[] and pricingLink (default "faq"). */
  prefix?: string;
  className?: string;
}

/** FAQ accordion (one open at a time, first open) used by the vertical landing pages. */
export function LandingFaq({ ns, prefix = 'faq', className = 'bg-surface-2' }: LandingFaqProps) {
  const { t } = useTranslation(ns);
  const { getStartingPrice } = useDetectedCurrency();
  const items = t(`${prefix}.items`, { returnObjects: true }) as FaqItem[];

  return (
    <Section id="faq" className={className}>
      <div className="grid items-start gap-7 lg:grid-cols-12 lg:gap-6">
        <div className="flex flex-col gap-4 lg:col-span-4">
          <Eyebrow>{t(`${prefix}.eyebrow`)}</Eyebrow>
          <SectionTitle>{t(`${prefix}.title`)}</SectionTitle>
        </div>
        <Accordion type="single" collapsible defaultValue="faq-0" className="flex flex-col gap-3 lg:col-span-8 lg:col-start-5">
          {items.map((item, i) => {
            const hasPrice = item.answer.includes(PRICE_PLACEHOLDER);
            return (
              <AccordionItem key={item.question} value={`faq-${i}`} className="rounded-2xl border border-line bg-surface px-[22px]">
                <AccordionTrigger className="min-h-16 gap-4 py-[18px] text-left font-display text-base font-semibold text-ink hover:no-underline lg:text-lg [&>svg]:h-5 [&>svg]:w-5 [&>svg]:text-brand-500">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="pb-5 pr-2 lg:pr-12">
                  <p className="m-0 max-w-[720px] text-base leading-[1.65] text-soft">
                    {hasPrice ? item.answer.replace(PRICE_PLACEHOLDER, getStartingPrice()) : item.answer}
                  </p>
                  {hasPrice && (
                    <Link
                      href="/pricing"
                      className="mt-3 inline-flex min-h-touch-target items-center gap-1.5 text-[15px] font-semibold text-brand-700 hover:text-brand-900"
                    >
                      {t(`${prefix}.pricingLink`)} <ArrowRight className="h-4 w-4" aria-hidden="true" />
                    </Link>
                  )}
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </div>
    </Section>
  );
}
